import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Input,
  Select,
  Td,
  Tr,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CloseIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { FaHandshake } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { PLATFORM_ROLES, BU_ROLES } from "../../../constants/roles";
import { listNeeds } from "../../../api/needsApi";
import { listClients } from "../../../api/clientsApi";
import {
  deleteMatch,
  listMatchesGroupedByConsultant,
  markMatchClientRejected,
  submitMatchToClient,
  transitionMatchWorkflow,
  updateMatch,
  withdrawMatchSubmission,
} from "../../../api/matchesApi";
import {
  NEED_STATUSES,
  NEED_STATUS_LABELS,
  NEED_STATUS_LABEL_KEYS,
  NEED_STATUS_COLORS,
  CONSULTANT_STAFFING_STATUS_LABELS,
  CONSULTANT_STAFFING_STATUS_LABEL_KEYS,
  CONSULTANT_STAFFING_STATUS_COLORS,
  MATCH_WORKFLOW_STATUSES,
  MATCH_WORKFLOW_STATUS_LABELS,
  MATCH_WORKFLOW_STATUS_LABEL_KEYS,
  MATCH_WORKFLOW_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  FilterBar,
  FilterSearch,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import MatchDetailModal from "./MatchDetailModal";
import NeedDetailModal from "./NeedDetailModal";

// Compact filter Select matching the template's 31px controls.
const filterSelectSx = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  lineHeight: "31px !important",
  fontSize: "12px",
  fontWeight: 600,
};

// Submission-status badge colours map onto theme-registered colour scales.
const getSubmissionBadgeColor = (status) => {
  if (status === "submitted_to_client") return "green";
  if (status === "rejected") return "red";
  if (status === "withdrawn") return "orange";
  return "gray";
};

const scorePill = (score) => {
  if (score == null) return "gray";
  if (score >= 70) return "green";
  if (score >= 40) return "yellow";
  return "red";
};

const Matches = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { effectiveRole, selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();
  const matchDetailModal = useDisclosure();
  const needDetailModal = useDisclosure();
  const submitModal = useDisclosure();
  const deleteDialog = useDisclosure();

  const canDelete = useMemo(() => {
    return (
      effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
      effectiveRole === BU_ROLES.ADMIN ||
      effectiveRole === BU_ROLES.MANAGER
    );
  }, [effectiveRole]);

  // View mode: "byNeed" (default) | "byConsultant"
  const [viewMode, setViewMode] = useState("byNeed");

  // Consultant-centric state
  const [consultantGroups, setConsultantGroups] = useState([]);
  const [loadingConsultantView, setLoadingConsultantView] = useState(false);
  const [expandedConsultantId, setExpandedConsultantId] = useState(null);
  const [consultantSearch, setConsultantSearch] = useState("");

  const [loadingNeeds, setLoadingNeeds] = useState(false);
  const [needs, setNeeds] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPagesFromApi, setTotalPagesFromApi] = useState(null);

  // Filters (apply on needs)
  const [search, setSearch] = useState("");
  const [filterClientId, setFilterClientId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterHasMatches, setFilterHasMatches] = useState(""); // "" | "with" | "without"
  const [clients, setClients] = useState([]);

  // Matches state
  const [expandedNeedId, setExpandedNeedId] = useState(null);
  const [updatingMatchId, setUpdatingMatchId] = useState(null);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [selectedNeedId, setSelectedNeedId] = useState(null);
  const [submittingMatchId, setSubmittingMatchId] = useState(null);
  const [transitioningMatchId, setTransitioningMatchId] = useState(null);
  const [submitTarget, setSubmitTarget] = useState(null);
  const [submitChannel, setSubmitChannel] = useState("email");
  const [submitNote, setSubmitNote] = useState("");
  const [submitAt, setSubmitAt] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingMatch, setDeletingMatch] = useState(false);

  const fetchNeeds = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setLoadingNeeds(true);
    }
    try {
      const params = {
        search: search || undefined,
        clientId: filterClientId || undefined,
        status: filterStatus || undefined,
        // backend hint (if supported). UI will also filter client-side.
        hasMatches:
          filterHasMatches === "with"
            ? true
            : filterHasMatches === "without"
              ? false
              : undefined,
        includeMatches: true,
        page,
        pageSize
      };
      const res = await listNeeds(params, { buId: selectedBusinessUnitId || undefined });
      setNeeds(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPagesFromApi(typeof res.totalPages === "number" ? res.totalPages : null);
    } catch (e) {
      toast({ title: t("pilotMatches.toasts.loadNeedsFailed"), status: "error" });
      setNeeds([]);
      setTotalItems(0);
      setTotalPagesFromApi(null);
    } finally {
      if (!silent) {
        setLoadingNeeds(false);
      }
    }
  }, [
    search,
    filterClientId,
    filterStatus,
    filterHasMatches,
    page,
    pageSize,
    selectedBusinessUnitId,
    toast,
    t
  ]);

  const fetchConsultantGroups = useCallback(async () => {
    setLoadingConsultantView(true);
    try {
      const params = {
        search: consultantSearch || undefined,
      };
      const res = await listMatchesGroupedByConsultant(params, { buId: selectedBusinessUnitId || undefined });
      setConsultantGroups(res?.items || []);
    } catch (e) {
      toast({ title: t("pilotMatches.toasts.loadNeedsFailed"), status: "error" });
      setConsultantGroups([]);
    } finally {
      setLoadingConsultantView(false);
    }
  }, [consultantSearch, selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    if (!isBUContextLoaded || viewMode !== "byConsultant") return;
    fetchConsultantGroups();
  }, [fetchConsultantGroups, isBUContextLoaded, viewMode]);

  const filteredNeeds = useMemo(() => {
    if (!filterHasMatches) return needs;
    return (needs || []).filter((n) => {
      const count = Array.isArray(n.matches) ? n.matches.length : 0;
      return filterHasMatches === "with" ? count > 0 : count === 0;
    });
  }, [filterHasMatches, needs]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    fetchNeeds();
  }, [fetchNeeds, isBUContextLoaded]);

  // Initial load of clients for filter
  useEffect(() => {
    if (!isBUContextLoaded) return;
    listClients({ page: 1, pageSize: 1000 }, { buId: selectedBusinessUnitId || undefined })
      .then((res) => setClients(res?.items || []))
      .catch(() => setClients([]));
  }, [isBUContextLoaded, selectedBusinessUnitId]);

  // Auto-refresh every 30s (needs with embedded matches)
  useEffect(() => {
    if (!isBUContextLoaded) return;
    const intervalId = setInterval(() => {
      // Silent refresh so the table does not visibly reload
      fetchNeeds({ silent: true });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchNeeds, isBUContextLoaded]);

  const handleToggleExpand = (needId) => {
    const willExpand = expandedNeedId !== needId;
    setExpandedNeedId(willExpand ? needId : null);
  };

  const handleOpenMatchDetail = (match, need) => {
    if (!match?.id) return;
    setSelectedMatchId(match.id);
    setSelectedMatch(match);
    setSelectedNeed(need || null);
    matchDetailModal.onOpen();
  };

  const handleOpenNeedDetail = (need) => {
    if (!need?.id) return;
    setSelectedNeedId(need.id);
    needDetailModal.onOpen();
  };

  const parseDateValue = (v) => {
    if (!v) return null;
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const d = new Date(`${v}T00:00:00Z`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    try {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const calcOpenDurationDays = (need) => {
    const start =
      parseDateValue(need?.created_at) ||
      parseDateValue(need?.createdAt) ||
      parseDateValue(need?.date);
    if (!start) return null;

    const isClosed =
      need?.status === "closed_need_closed" ||
      need?.status === "abandoned" ||
      need?.status === "postponed";

    const end = isClosed
      ? parseDateValue(need?.updated_at) ||
        parseDateValue(need?.updatedAt) ||
        parseDateValue(need?.endDate) ||
        parseDateValue(need?.end_date) ||
        new Date()
      : new Date();

    const days = Math.floor((end.getTime() - start.getTime()) / 86400000);
    return Math.max(0, days);
  };

  const isMatchPositioned = (m) => {
    return Boolean(
      m?.positioned ??
        m?.isPositioned ??
        m?.is_positioned ??
        m?.salesValidated ??
        m?.sales_validated
    );
  };

  const getMatchSubmissionStatus = (m) => {
    const status = m?.submissionStatus ?? m?.submission_status;
    return status || "draft";
  };

  const getMatchWorkflowStatus = (m) => {
    return m?.workflowStatus ?? m?.workflow_status ?? null;
  };

  const getNextWorkflowStep = (current) => {
    if (!current) return MATCH_WORKFLOW_STATUSES[0]?.value || null;
    const idx = MATCH_WORKFLOW_STATUSES.findIndex((s) => s.value === current);
    if (idx < 0 || idx >= MATCH_WORKFLOW_STATUSES.length - 1) return null;
    return MATCH_WORKFLOW_STATUSES[idx + 1].value;
  };

  const handleWorkflowAdvance = async (match, targetStatus) => {
    if (!match?.id || !targetStatus) return;
    setTransitioningMatchId(match.id);
    try {
      const updated = await transitionMatchWorkflow(
        match.id,
        targetStatus,
        false,
        { buId: selectedBusinessUnitId || undefined }
      );
      // Update match in local state
      setNeeds((prev) =>
        prev.map((need) => ({
          ...need,
          matches: (need.matches || []).map((m) =>
            (m.id || m.matchId) === match.id
              ? { ...m, ...updated }
              : m
          )
        }))
      );
      toast({
        title: t("pilotMatches.detail.workflow.toasts.transitionSuccess", {
          defaultValue: "Workflow updated"
        }),
        status: "success"
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast({
        title: t("pilotMatches.detail.workflow.toasts.transitionFailedTitle", {
          defaultValue: "Transition failed"
        }),
        description: detail,
        status: "error"
      });
    } finally {
      setTransitioningMatchId(null);
    }
  };

  const getSubmissionStatusLabel = (status) => {
    if (status === "submitted_to_client") {
      return t("pilotMatches.detail.submission.status.submittedToClient");
    }
    if (status === "rejected") {
      return t("pilotMatches.detail.submission.status.rejected");
    }
    if (status === "withdrawn") {
      return t("pilotMatches.detail.submission.status.withdrawn");
    }
    return t("pilotMatches.detail.submission.status.draft");
  };

  const formatSubmittedAt = (v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString();
    } catch {
      return null;
    }
  };

  const updateMatchInNeeds = useCallback((needId, matchId, updater) => {
    setNeeds((prev) =>
      (prev || []).map((n) => {
        if (n.id !== needId) return n;
        const currentMatches = Array.isArray(n.matches) ? n.matches : [];
        return {
          ...n,
          matches: currentMatches.map((m) =>
            m.id === matchId ? updater(m) : m
          )
        };
      })
    );
  }, []);

  const showSubmissionTransitionError = (err) => {
    const code = err?.response?.data?.code;
    if (code === "INVALID_SUBMISSION_TRANSITION") {
      toast({
        title: t("pilotMatches.toasts.submission.invalidTransitionTitle"),
        description: t("pilotMatches.toasts.submission.invalidTransitionDescription"),
        status: "warning"
      });
      return;
    }
    if (code === "INVALID_SUBMISSION_CHANNEL") {
      toast({
        title: t("pilotMatches.toasts.submission.invalidChannelTitle"),
        description: t("pilotMatches.toasts.submission.invalidChannelDescription"),
        status: "error"
      });
      return;
    }
    if (code === "INVALID_SUBMITTED_AT") {
      toast({
        title: t("pilotMatches.toasts.submission.invalidSubmittedAtTitle"),
        status: "error"
      });
      return;
    }
    if (code === "MATCH_NOT_FOUND") {
      toast({
        title: t("pilotMatches.toasts.submission.matchNotFoundTitle"),
        status: "error"
      });
      return;
    }
    toast({
      title: t("pilotMatches.toasts.submission.genericFailedTitle"),
      description: err?.response?.data?.message || undefined,
      status: "error"
    });
  };

  const handleOpenSubmitModal = (needId, match, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!match?.id) return;
    setSubmitTarget({ needId, matchId: match.id, consultantName: match.consultantName || "" });
    setSubmitChannel("email");
    setSubmitNote("");
    setSubmitAt("");
    submitModal.onOpen();
  };

  const handleConfirmSubmitToClient = async () => {
    if (!submitTarget?.matchId || !submitTarget?.needId) return;
    const payload = {
      submissionChannel: submitChannel,
      submissionNote: submitNote || null
    };
    if (submitAt) {
      const parsed = new Date(submitAt);
      if (Number.isNaN(parsed.getTime())) {
      toast({
        title: t("pilotMatches.toasts.submission.invalidSubmittedAtTitle"),
        status: "error"
      });
        return;
      }
      payload.submittedAt = parsed.toISOString();
    }

    setSubmittingMatchId(submitTarget.matchId);
    try {
      const updated = await submitMatchToClient(
        submitTarget.matchId,
        payload,
        { buId: selectedBusinessUnitId || undefined }
      );
      updateMatchInNeeds(submitTarget.needId, submitTarget.matchId, (m) => ({
        ...m,
        ...(updated || {}),
        submissionStatus: updated?.submissionStatus || "submitted_to_client",
        submissionChannel: updated?.submissionChannel || submitChannel,
        submissionNote:
          updated?.submissionNote != null ? updated.submissionNote : submitNote || null,
        submittedAt: updated?.submittedAt || new Date().toISOString()
      }));
      toast({
        title: t("pilotMatches.toasts.submission.submitSuccess"),
        status: "success"
      });
      submitModal.onClose();
      setSubmitTarget(null);
    } catch (err) {
      showSubmissionTransitionError(err);
    } finally {
      setSubmittingMatchId(null);
    }
  };

  const handleWithdrawSubmission = async (needId, match, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!match?.id) return;
    setTransitioningMatchId(match.id);
    try {
      const updated = await withdrawMatchSubmission(
        match.id,
        { buId: selectedBusinessUnitId || undefined }
      );
      updateMatchInNeeds(needId, match.id, (m) => ({
        ...m,
        ...(updated || {}),
        submissionStatus: updated?.submissionStatus || "withdrawn"
      }));
      toast({
        title: t("pilotMatches.toasts.submission.withdrawSuccess"),
        status: "success"
      });
    } catch (err) {
      showSubmissionTransitionError(err);
    } finally {
      setTransitioningMatchId(null);
    }
  };

  const handleMarkRejected = async (needId, match, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!match?.id) return;
    setTransitioningMatchId(match.id);
    try {
      const updated = await markMatchClientRejected(
        match.id,
        { buId: selectedBusinessUnitId || undefined }
      );
      updateMatchInNeeds(needId, match.id, (m) => ({
        ...m,
        ...(updated || {}),
        submissionStatus: updated?.submissionStatus || "rejected"
      }));
      toast({
        title: t("pilotMatches.toasts.submission.rejectSuccess"),
        status: "success"
      });
    } catch (err) {
      showSubmissionTransitionError(err);
    } finally {
      setTransitioningMatchId(null);
    }
  };

  const handleRequestDeleteMatch = (needId, match, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!canDelete || !match?.id) return;
    setDeleteTarget({ needId, matchId: match.id, consultantName: match.consultantName || "" });
    deleteDialog.onOpen();
  };

  const handleConfirmDeleteMatch = async () => {
    if (!deleteTarget?.matchId) return;
    const { needId, matchId } = deleteTarget;
    setDeletingMatch(true);
    try {
      await deleteMatch(matchId, { buId: selectedBusinessUnitId || undefined });
      setNeeds((prev) =>
        (prev || []).map((n) => {
          if (n.id !== needId) return n;
          const current = Array.isArray(n.matches) ? n.matches : [];
          const remaining = current.filter((m) => m.id !== matchId);
          return {
            ...n,
            matches: remaining
          };
        })
      );
      toast({
        title: t("pilotMatches.toasts.deleteSuccess"),
        status: "success"
      });
      setDeleteTarget(null);
      deleteDialog.onClose();
    } catch (err) {
      toast({
        title: t("pilotMatches.toasts.deleteFailedTitle"),
        description:
          err?.response?.data?.message ||
          t("pilotMatches.toasts.deleteFailedDescription"),
        status: "error"
      });
    } finally {
      setDeletingMatch(false);
    }
  };

  const handleTogglePositioned = async (needId, match, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!match?.id) return;

    const current = isMatchPositioned(match);
    const next = !current;

    // Optimistic update
    setNeeds((prev) =>
      (prev || []).map((n) => {
        if (n.id !== needId) return n;
        const currentMatches = Array.isArray(n.matches) ? n.matches : [];
        return {
          ...n,
          matches: currentMatches.map((m) =>
            m.id === match.id ? { ...m, positioned: next } : m
          )
        };
      })
    );

    setUpdatingMatchId(match.id);
    try {
      await updateMatch(
        match.id,
        { positioned: next },
        { buId: selectedBusinessUnitId || undefined }
      );
      toast({
        title: t("pilotMatches.toasts.updateMatchSuccess"),
        status: "success"
      });
    } catch (err) {
      // Revert on failure
      setNeeds((prev) =>
        (prev || []).map((n) => {
          if (n.id !== needId) return n;
          const currentMatches = Array.isArray(n.matches) ? n.matches : [];
          return {
            ...n,
            matches: currentMatches.map((m) =>
              m.id === match.id ? { ...m, positioned: current } : m
            )
          };
        })
      );
      toast({
        title: t("pilotMatches.toasts.updateMatchFailed"),
        description: err?.response?.data?.message || undefined,
        status: "error"
      });
    } finally {
      setUpdatingMatchId(null);
    }
  };

  const totalPages = useMemo(() => {
    return totalPagesFromApi || Math.max(1, Math.ceil(totalItems / pageSize));
  }, [totalPagesFromApi, totalItems, pageSize]);

  // Tiny uppercase header label for the inner (per-need) match table.
  const innerTh = (label, align = "left") => (
    <Box
      as="th"
      textAlign={align}
      px="10px"
      py="6px"
      fontSize="10px"
      fontWeight="900"
      letterSpacing="0.055em"
      textTransform="uppercase"
      color="portal.muted"
      borderBottomWidth="1px"
      borderColor="portal.line"
      whiteSpace="nowrap"
    >
      {label}
    </Box>
  );

  // ---- Need-centric columns (DataTable) ----
  const needColumns = [
    { key: "expand", label: "", width: "44px" },
    { key: "date", label: t("pilotNeeds.list.table.date"), width: "10%" },
    { key: "client", label: t("pilotNeeds.list.table.client"), width: "16%" },
    { key: "title", label: t("pilotNeeds.list.table.title"), width: "22%" },
    { key: "status", label: t("pilotNeeds.list.table.status"), width: "14%" },
    { key: "openDuration", label: t("pilotMatches.list.table.openDuration"), width: "12%", align: "right" },
    { key: "suggested", label: t("pilotMatches.list.table.suggestedProfiles"), width: "11%", align: "right" },
    { key: "positioned", label: t("pilotMatches.list.table.positionedProfiles"), width: "11%", align: "right" },
  ];

  const renderInnerMatches = (need, sortedMatches) => (
    <Box
      as="table"
      w="100%"
      sx={{
        borderCollapse: "collapse",
        tableLayout: "auto",
        // Neutralise the parent DataTable's `& tbody td` rules (43px height,
        // nowrap, ellipsis) that would otherwise cascade into these inner cells.
        "& td, & th": {
          height: "auto !important",
          whiteSpace: "normal !important",
          textOverflow: "clip !important",
          overflow: "visible !important",
        },
      }}
    >
      <Box as="thead">
        <Box as="tr">
          {innerTh(t("pilotMatches.detail.table.consultant"))}
          {innerTh(t("pilotMatches.detail.table.score"))}
          {innerTh(t("pilotMatches.detail.table.source"))}
          {innerTh(t("pilotMatches.detail.table.reasoning"))}
          {innerTh(t("pilotMatches.detail.submission.columnTitle"))}
          {innerTh(t("pilotMatches.detail.workflow.columnTitle", { defaultValue: "Workflow" }))}
          {innerTh(t("pilotMatches.detail.table.positioned"))}
          {innerTh(t("pilotMatches.detail.table.actions"), "right")}
        </Box>
      </Box>
      <Box as="tbody">
        {sortedMatches.map((m) => {
          const submissionStatus = getMatchSubmissionStatus(m);
          const wfs = getMatchWorkflowStatus(m);
          const next = getNextWorkflowStep(wfs);
          const submittedAt = formatSubmittedAt(m?.submittedAt ?? m?.submitted_at);
          const positioned = isMatchPositioned(m);
          return (
            <Box
              as="tr"
              key={m.id}
              cursor="pointer"
              onClick={() => handleOpenMatchDetail(m, need)}
              sx={{ "&:hover td, &:hover": { bg: "#FBFEFE" } }}
              _hover={{ bg: "#FBFEFE" }}
            >
              <Box as="td" px="10px" py="8px" fontSize="12px" fontWeight="600" color="portal.text" borderBottomWidth="1px" borderColor="#edf2f5">
                {m.consultantName || m.consultant_id}
              </Box>
              <Box as="td" px="10px" py="8px" borderBottomWidth="1px" borderColor="#edf2f5">
                {m.score != null ? (
                  <StatusBadge label={`${m.score}%`} colorScheme={scorePill(m.score)} />
                ) : (
                  <Text as="span" fontSize="12px" color="portal.muted">-</Text>
                )}
              </Box>
              <Box as="td" px="10px" py="8px" fontSize="12px" fontWeight="600" color="#243543" borderBottomWidth="1px" borderColor="#edf2f5">
                {m.source}
              </Box>
              <Box
                as="td"
                px="10px"
                py="8px"
                fontSize="12px"
                color="portal.muted"
                maxW="280px"
                borderBottomWidth="1px"
                borderColor="#edf2f5"
                sx={{
                  whiteSpace: "nowrap !important",
                  overflow: "hidden !important",
                  textOverflow: "ellipsis !important",
                }}
              >
                {m.reasoning || "-"}
              </Box>
              <Box as="td" px="10px" py="8px" borderBottomWidth="1px" borderColor="#edf2f5">
                <StatusBadge
                  label={getSubmissionStatusLabel(submissionStatus)}
                  colorScheme={getSubmissionBadgeColor(submissionStatus)}
                />
                {submittedAt && (
                  <Text fontSize="10px" color="portal.muted" mt="2px" sx={{ marginBottom: "0 !important" }}>
                    {submittedAt}
                  </Text>
                )}
              </Box>
              <Box as="td" px="10px" py="8px" borderBottomWidth="1px" borderColor="#edf2f5">
                <HStack spacing="6px">
                  {wfs ? (
                    <StatusBadge
                      label={getStatusLabel(t, MATCH_WORKFLOW_STATUS_LABEL_KEYS, MATCH_WORKFLOW_STATUS_LABELS, wfs)}
                      colorScheme={MATCH_WORKFLOW_STATUS_COLORS[wfs] || "gray"}
                    />
                  ) : (
                    <Text as="span" fontSize="12px" color="portal.muted">-</Text>
                  )}
                  {next && wfs !== "WITHDRAWN" && wfs !== "REJECTED" && (
                    <Button
                      size="xs"
                      h="22px"
                      minW="22px"
                      px="6px"
                      variant="secondary"
                      isLoading={transitioningMatchId === m.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWorkflowAdvance(m, next);
                      }}
                      title={getStatusLabel(t, MATCH_WORKFLOW_STATUS_LABEL_KEYS, MATCH_WORKFLOW_STATUS_LABELS, next)}
                    >
                      {"→"}
                    </Button>
                  )}
                </HStack>
              </Box>
              <Box as="td" px="10px" py="8px" borderBottomWidth="1px" borderColor="#edf2f5">
                <Button
                  size="xs"
                  h="25px"
                  leftIcon={positioned ? <CheckIcon boxSize="9px" /> : <CloseIcon boxSize="8px" />}
                  variant={positioned ? "primary" : "secondary"}
                  isLoading={updatingMatchId === m.id}
                  onClick={(e) => handleTogglePositioned(need.id, m, e)}
                >
                  {positioned
                    ? t("pilotMatches.detail.positioned.yes")
                    : t("pilotMatches.detail.positioned.no")}
                </Button>
              </Box>
              <Box as="td" px="10px" py="8px" borderBottomWidth="1px" borderColor="#edf2f5">
                <HStack justify="flex-end" spacing="6px">
                  {submissionStatus === "draft" && (
                    <Button
                      size="xs"
                      h="25px"
                      variant="primary"
                      isLoading={submittingMatchId === m.id}
                      onClick={(e) => handleOpenSubmitModal(need.id, m, e)}
                    >
                      {t("pilotMatches.detail.submission.submitAction")}
                    </Button>
                  )}
                  {submissionStatus === "submitted_to_client" && (
                    <>
                      <Button
                        size="xs"
                        h="25px"
                        variant="secondary"
                        isLoading={transitioningMatchId === m.id}
                        onClick={(e) => handleWithdrawSubmission(need.id, m, e)}
                      >
                        {t("pilotMatches.detail.submission.withdrawAction")}
                      </Button>
                      <Button
                        size="xs"
                        h="25px"
                        variant="secondary"
                        color="portal.danger"
                        borderColor="portal.danger"
                        isLoading={transitioningMatchId === m.id}
                        onClick={(e) => handleMarkRejected(need.id, m, e)}
                      >
                        {t("pilotMatches.detail.submission.rejectAction")}
                      </Button>
                    </>
                  )}
                  <MiniIconButton
                    aria-label={t("pilotMatches.detail.actions.delete")}
                    icon={<DeleteIcon />}
                    color="portal.danger"
                    _hover={{ bg: "red.50", borderColor: "portal.danger", color: "portal.danger" }}
                    isDisabled={!canDelete}
                    onClick={(e) => handleRequestDeleteMatch(need.id, m, e)}
                  />
                </HStack>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const renderNeedRow = (need) => {
    const isExpanded = expandedNeedId === need.id;
    const matches = Array.isArray(need.matches) ? need.matches : [];
    const suggestedProfilesCount = matches.length;
    const positionedProfilesCount = matches.filter(isMatchPositioned).length;
    const sortedMatches = [...matches].sort(
      (a, b) => (Number(b.score) || 0) - (Number(a.score) || 0)
    );
    const openDurationDays = calcOpenDurationDays(need);

    return (
      <React.Fragment key={need.id}>
        <Tr
          onClick={() => handleOpenNeedDetail(need)}
          cursor="pointer"
        >
          <Td onClick={(e) => e.stopPropagation()}>
            <MiniIconButton
              aria-label={
                isExpanded
                  ? t("pilotMatches.list.expand.hide")
                  : t("pilotMatches.list.expand.show")
              }
              icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand(need.id);
              }}
            />
          </Td>
          <Td>{need.date || ""}</Td>
          <Td>{need.clientName || ""}</Td>
          <Td fontWeight="700" color="portal.text">{need.title}</Td>
          <Td>
            <StatusBadge
              label={getStatusLabel(t, NEED_STATUS_LABEL_KEYS, NEED_STATUS_LABELS, need.status)}
              colorScheme={NEED_STATUS_COLORS[need.status] || "gray"}
            />
          </Td>
          <Td textAlign="right">{openDurationDays != null ? openDurationDays : "-"}</Td>
          <Td textAlign="right">{suggestedProfilesCount}</Td>
          <Td textAlign="right">{positionedProfilesCount}</Td>
        </Tr>
        {isExpanded && (
          <Tr>
            <Td
              colSpan={needColumns.length}
              bg="#F7FAFC"
              px="16px"
              py="12px"
              sx={{ height: "auto !important", whiteSpace: "normal !important" }}
            >
              {sortedMatches.length === 0 ? (
                <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                  {t("pilotMatches.detail.noMatches")}
                </Text>
              ) : (
                <Box overflowX="auto" bg="white" borderWidth="1px" borderColor="portal.line" borderRadius="10px" p="2px">
                  {renderInnerMatches(need, sortedMatches)}
                </Box>
              )}
            </Td>
          </Tr>
        )}
      </React.Fragment>
    );
  };

  // ---- Consultant-centric columns (DataTable) ----
  const consultantColumns = [
    { key: "expand", label: "", width: "44px" },
    { key: "consultant", label: t("pilotMatches.list.matchTable.consultant", { defaultValue: "Consultant" }), width: "22%" },
    { key: "title", label: t("pilotConsultants.list.table.title", { defaultValue: "Title" }), width: "22%" },
    { key: "status", label: t("pilotConsultants.list.table.staffingStatus", { defaultValue: "Status" }), width: "16%" },
    { key: "bestScore", label: t("pilotMatches.list.matchTable.bestScore", { defaultValue: "Best Score" }), width: "12%" },
    { key: "matches", label: t("pilotMatches.list.table.matchesCount"), width: "16%" },
  ];

  const renderConsultantRow = (group) => {
    const isExpanded = expandedConsultantId === group.consultantId;
    const wonCount = group.matches.filter((m) => m.workflowStatus === "WON").length;
    return (
      <React.Fragment key={group.consultantId}>
        <Tr
          cursor="pointer"
          onClick={() =>
            setExpandedConsultantId(isExpanded ? null : group.consultantId)
          }
        >
          <Td onClick={(e) => e.stopPropagation()}>
            <MiniIconButton
              aria-label={
                isExpanded
                  ? t("pilotMatches.list.expand.hide")
                  : t("pilotMatches.list.expand.show")
              }
              icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              onClick={() =>
                setExpandedConsultantId(isExpanded ? null : group.consultantId)
              }
            />
          </Td>
          <Td fontWeight="700" color="portal.text">{group.consultantName}</Td>
          <Td color="portal.muted">{group.consultantTitle || "-"}</Td>
          <Td>
            {group.staffingStatus ? (
              <StatusBadge
                label={getStatusLabel(t, CONSULTANT_STAFFING_STATUS_LABEL_KEYS, CONSULTANT_STAFFING_STATUS_LABELS, group.staffingStatus)}
                colorScheme={CONSULTANT_STAFFING_STATUS_COLORS[group.staffingStatus] || "gray"}
              />
            ) : "-"}
          </Td>
          <Td>
            {group.bestScore != null ? (
              <StatusBadge label={`${group.bestScore}%`} colorScheme={scorePill(group.bestScore)} />
            ) : "-"}
          </Td>
          <Td>
            <HStack spacing="6px">
              <StatusBadge label={String(group.matches.length)} colorScheme="blue" />
              {wonCount > 0 && (
                <StatusBadge
                  label={`${wonCount} ${t("pilotMatches.list.matchTable.won", { defaultValue: "won" })}`}
                  colorScheme="green"
                />
              )}
            </HStack>
          </Td>
        </Tr>
        {isExpanded &&
          group.matches.map((match) => (
            <Tr
              key={match.id}
              bg="#F7FAFC"
              cursor="pointer"
              onClick={() => handleOpenMatchDetail(match, null)}
            >
              <Td></Td>
              <Td color="portal.muted">{match.client_name || ""}</Td>
              <Td fontWeight="600">{match.need_title || ""}</Td>
              <Td>
                {match.workflowStatus && (
                  <StatusBadge
                    label={getStatusLabel(t, MATCH_WORKFLOW_STATUS_LABEL_KEYS, MATCH_WORKFLOW_STATUS_LABELS, match.workflowStatus)}
                    colorScheme={MATCH_WORKFLOW_STATUS_COLORS[match.workflowStatus] || "gray"}
                  />
                )}
              </Td>
              <Td>
                {match.score != null && (
                  <StatusBadge label={`${match.score}%`} colorScheme={scorePill(match.score)} />
                )}
              </Td>
              <Td>
                {match.submissionStatus === "submitted_to_client" && (
                  <StatusBadge
                    label={t("pilotMatches.list.matchTable.submitted", { defaultValue: "Submitted" })}
                    colorScheme="purple"
                  />
                )}
              </Td>
            </Tr>
          ))}
      </React.Fragment>
    );
  };

  const viewSwitcher = (
    <Select
      value={viewMode}
      onChange={(e) => setViewMode(e.target.value)}
      w="180px"
      aria-label={t("pilotMatches.list.viewSwitcher.ariaLabel", { defaultValue: "Data view" })}
      sx={filterSelectSx}
    >
      <option value="byNeed">{t("pilotMatches.list.viewSwitcher.byNeed")}</option>
      <option value="byConsultant">{t("pilotMatches.list.viewSwitcher.byConsultant")}</option>
    </Select>
  );

  return (
    // Bleed past PortalLayout's padding so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="MA"
        eyebrow={t("pilotMatches.list.eyebrow", "ASAP PILOT")}
        title={t("pilotMatches.list.pageTitle")}
        actions={viewSwitcher}
      />

      <Box p="20px">
        {viewMode === "byConsultant" ? (
          <Panel
            title={t("pilotMatches.list.pageTitle")}
            count={
              loadingConsultantView
                ? t("pilotMatches.list.summary.loading")
                : t("pilotMatches.list.summary.consultantCount", { count: consultantGroups.length })
            }
            filters={
              <FilterBar>
                <FilterSearch
                  placeholder={t("pilotMatches.list.filters.searchConsultantPlaceholder")}
                  value={consultantSearch}
                  onChange={(e) => setConsultantSearch(e.target.value)}
                />
              </FilterBar>
            }
          >
            <DataTable
              columns={consultantColumns}
              rows={consultantGroups}
              loading={loadingConsultantView}
              renderRow={renderConsultantRow}
              emptyState={
                <EmptyState
                  icon={<FaHandshake />}
                  title={t("pilotMatches.list.table.noItems")}
                  hint={t("pilotMatches.list.emptyHint", "Generate matches to position consultants against client needs.")}
                />
              }
            />
          </Panel>
        ) : (
          <Panel
            title={t("pilotMatches.list.pageTitle")}
            count={
              loadingNeeds
                ? t("pilotMatches.list.summary.loading")
                : t("pilotMatches.list.summary.total", { count: totalItems })
            }
            filters={
              <FilterBar>
                <FilterSearch
                  placeholder={t("pilotMatches.list.filters.searchPlaceholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
                <Select
                  placeholder={t("pilotMatches.list.filters.clientPlaceholder")}
                  value={filterClientId}
                  onChange={(e) => {
                    setFilterClientId(e.target.value);
                    setPage(1);
                  }}
                  w="auto"
                  minW="180px"
                  sx={filterSelectSx}
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                <Select
                  placeholder={t("pilotMatches.list.filters.statusPlaceholder")}
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPage(1);
                  }}
                  w="auto"
                  minW="180px"
                  sx={filterSelectSx}
                >
                  {NEED_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {getStatusLabel(t, NEED_STATUS_LABEL_KEYS, NEED_STATUS_LABELS, s.value)}
                    </option>
                  ))}
                </Select>
                <Select
                  placeholder={t("pilotMatches.list.filters.hasMatchesPlaceholder")}
                  value={filterHasMatches}
                  onChange={(e) => {
                    setFilterHasMatches(e.target.value);
                    setPage(1);
                  }}
                  w="auto"
                  minW="170px"
                  sx={filterSelectSx}
                >
                  <option value="with">{t("pilotMatches.list.filters.hasMatchesWith")}</option>
                  <option value="without">{t("pilotMatches.list.filters.hasMatchesWithout")}</option>
                </Select>
              </FilterBar>
            }
            footer={
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={(sz) => {
                  setPageSize(sz);
                  setPage(1);
                }}
              />
            }
          >
            <DataTable
              columns={needColumns}
              rows={filteredNeeds}
              loading={loadingNeeds}
              renderRow={renderNeedRow}
              emptyState={
                <EmptyState
                  icon={<FaHandshake />}
                  title={t("pilotMatches.list.table.noItems")}
                  hint={t("pilotMatches.list.emptyHint", "Generate matches to position consultants against client needs.")}
                />
              }
            />
          </Panel>
        )}
      </Box>

      <MatchDetailModal
        isOpen={matchDetailModal.isOpen}
        onClose={() => {
          setSelectedMatchId(null);
          setSelectedMatch(null);
          setSelectedNeed(null);
          matchDetailModal.onClose();
        }}
        matchId={selectedMatchId}
        initialMatch={selectedMatch}
        initialNeed={selectedNeed}
      />
      <NeedDetailModal
        isOpen={needDetailModal.isOpen}
        onClose={() => {
          setSelectedNeedId(null);
          needDetailModal.onClose();
        }}
        needId={selectedNeedId}
        shouldNavigateOnClose={false}
      />

      <Modal
        isOpen={submitModal.isOpen}
        onClose={() => {
          setSubmitTarget(null);
          submitModal.onClose();
        }}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotMatches.detail.submission.modalTitle")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <Text fontSize="12px" color="portal.muted" mb="12px" sx={{ marginBottom: "12px !important" }}>
              {t("pilotMatches.detail.submission.modalDescription")}
            </Text>
            <Box mb="12px">
              <Text
                fontSize="10px"
                textTransform="uppercase"
                fontWeight="800"
                letterSpacing="0.055em"
                color="portal.muted"
                mb="4px"
                sx={{ marginBottom: "4px !important" }}
              >
                {t("pilotMatches.detail.submission.channelLabel")}
              </Text>
              <Select
                value={submitChannel}
                onChange={(e) => setSubmitChannel(e.target.value)}
                sx={filterSelectSx}
              >
                <option value="email">{t("pilotMatches.detail.submission.channelEmail")}</option>
                <option value="platform">{t("pilotMatches.detail.submission.channelPlatform")}</option>
                <option value="other">{t("pilotMatches.detail.submission.channelOther")}</option>
              </Select>
            </Box>
            <Box mb="12px">
              <Text
                fontSize="10px"
                textTransform="uppercase"
                fontWeight="800"
                letterSpacing="0.055em"
                color="portal.muted"
                mb="4px"
                sx={{ marginBottom: "4px !important" }}
              >
                {t("pilotMatches.detail.submission.submittedAtLabel")}
              </Text>
              <Input
                type="datetime-local"
                value={submitAt}
                onChange={(e) => setSubmitAt(e.target.value)}
                h="31px"
                fontWeight="600"
              />
            </Box>
            <Box>
              <Text
                fontSize="10px"
                textTransform="uppercase"
                fontWeight="800"
                letterSpacing="0.055em"
                color="portal.muted"
                mb="4px"
                sx={{ marginBottom: "4px !important" }}
              >
                {t("pilotMatches.detail.submission.noteLabel")}
              </Text>
              <Textarea
                rows={3}
                value={submitNote}
                onChange={(e) => setSubmitNote(e.target.value)}
                placeholder={t("pilotMatches.detail.submission.notePlaceholder")}
                fontSize="13px"
              />
            </Box>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setSubmitTarget(null);
                submitModal.onClose();
              }}
            >
              {t("pilotMatches.detail.submission.cancelAction")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirmSubmitToClient}
              isLoading={submittingMatchId === submitTarget?.matchId}
            >
              {t("pilotMatches.detail.submission.confirmAction")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => {
          if (deletingMatch) return;
          setDeleteTarget(null);
          deleteDialog.onClose();
        }}
        onConfirm={handleConfirmDeleteMatch}
        title={t("pilotMatches.list.deleteDialog.title", { defaultValue: "Delete match" })}
        body={t("pilotMatches.detail.confirmDelete")}
        confirmLabel={t("pilotMatches.detail.actions.delete")}
        isLoading={deletingMatch}
      />
    </Box>
  );
};

export default Matches;
